/**
 * Objective Feed Service
 *
 * Fetches the objective-grouped feed from GET /objective-feed.
 * Maps the backend's snake_case response to the camelCase frontend types
 * defined in types/objectiveFeed.ts.
 */

import ApiService from './ApiService';
import type { FeedPost } from './FeedService';
import type {
  ObjectiveFeedResponse,
  ObjectiveFeedGroup,
  Objective,
  ObjectiveProgressDetail,
  ObjectiveProgressDetailBlock,
  ObjectiveAction,
} from '@customTypes/objectiveFeed';

// ---------------------------------------------------------------------------
// Raw API response types (snake_case, matching backend contract)
// ---------------------------------------------------------------------------

// RawObjective and mapObjective are shared with ObjectiveService
export interface RawObjective {
  id: string;
  title: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export function mapObjective(raw: RawObjective): Objective {
  return {
    id: raw.id,
    title: raw.title,
    description: raw.description,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

interface RawProgressDetail {
  summary?: string;
  blocks: Array<{
    type: 'text' | 'chart' | 'chart_xml';
    title?: string;
    body?: string;
    xml?: string;
  }>;
}

interface RawAction {
  action_id: string;
  title: string;
  description?: string | null;
  objective_id: string;
  status: 'pending' | 'completed' | 'dismissed';
  created_at: string;
}

interface RawObjectiveFeedGroup {
  objective: RawObjective;
  progress: number;
  progress_detail?: RawProgressDetail;
  actions?: RawAction[];
  posts: FeedPost[];
  archived_updates?: FeedPost[];
  last_activity_at?: string;
}

interface RawObjectiveFeedResponse {
  objectives: RawObjectiveFeedGroup[];
  next_cursor: string | null;
}

function mapProgressDetail(raw: RawProgressDetail): ObjectiveProgressDetail {
  return {
    summary: raw.summary,
    blocks: raw.blocks as ObjectiveProgressDetailBlock[],
  };
}

function mapAction(raw: RawAction): ObjectiveAction {
  return {
    actionId: raw.action_id,
    title: raw.title,
    description: raw.description,
    objectiveId: raw.objective_id,
    status: raw.status,
    createdAt: raw.created_at,
  };
}

function mapGroup(raw: RawObjectiveFeedGroup): ObjectiveFeedGroup {
  return {
    objective: mapObjective(raw.objective),
    progress: raw.progress,
    progressDetail: raw.progress_detail
      ? mapProgressDetail(raw.progress_detail)
      : undefined,
    actions: (raw.actions ?? []).map(mapAction),
    posts: raw.posts,
    archivedUpdates: raw.archived_updates,
    lastActivityAt: raw.last_activity_at,
  };
}

function mapResponse(raw: RawObjectiveFeedResponse): ObjectiveFeedResponse {
  return {
    objectives: raw.objectives.map(mapGroup),
    nextCursor: raw.next_cursor,
  };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class ObjectiveFeedService {
  /**
   * Fetch the objective-grouped feed for the current workspace.
   *
   * @param limit  – max objectives to return (default decided by backend)
   * @param cursor – pagination cursor from a previous response
   */
  public async getObjectiveFeed(
    limit?: number,
    cursor?: string,
  ): Promise<ObjectiveFeedResponse> {
    const params: Record<string, any> = {};
    if (limit) params.limit = limit;
    if (cursor) params.cursor = cursor;

    const { success, data } = await ApiService.get<RawObjectiveFeedResponse>(
      '/objective-feed',
      params,
    );

    if (success && data) {
      return mapResponse(data);
    }

    return { objectives: [], nextCursor: null };
  }
}

export default new ObjectiveFeedService();
